import {
  faCircleCheck,
  faCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMediaQuery, useTheme } from '@mui/material';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { isOtpActive } from '!core/redux/dbConfigurations';
import {
  TrlFeedback,
  TrlScreenSummary,
  TrlTextWithLink,
} from '»components/atoms';
import { ButtonMain } from '»components/atoms/Buttons';
import { TrlTextField } from '»components/atoms/Inputs';
import { Otp, selectUserId } from '»components/Auth';
import { login as cryptoLogin, signUp as cryptoSignUp } from '»utils/crypto';

import { changePasswordServices } from './changePassword.services';
import {
  BoxStyled,
  FeedbackContainer,
  TypographySubtitleStyled,
  TypographyTextStyled,
  TypographyTitleStyled,
} from './TrlChangePassword.styles';
import { trlChangePasswordValidation } from './trlChangePassword.yup';

/**
 * Component for password change form with rule checklist.
 */
export const TrlChangePassword = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [passwordPolicies, setPasswordPolicies] = useState([]);
  const [feedbackMsg, setFeedbackMsg] = useState(null);
  const [submittedWithSuccess, setSubmittedWithSuccess] = useState(false);
  const [validationCode, setValidationCode] = useState('');
  const [passwordAtual, setPasswordAtual] = useState('');
  const [passwordNova, setPasswordNova] = useState('');
  const [passwordConfirmar, setPasswordConfirmar] = useState('');

  const useOtp = useSelector((state) =>
    isOtpActive(state, 'ENFORCE_OTP_CHANGE_PASSWORD'),
  );

  const validOTP = useOtp ? validationCode !== '' : true;

  const resetCurrentPassword = () => {
    setPasswordAtual('');
  };

  useEffect(() => {
    setTimeout(resetCurrentPassword, 100);
  }, []);

  const handleValueChange = (event) => {
    setValidationCode(event.target.value);
  };

  const handleGetOptError = (event) => {
    setFeedbackMsg(event);
  };

  /**
   * Fetches the password policies from the service and updates the state.
   */
  const fetchPasswordPolicies = () => {
    changePasswordServices.getPasswordPolicies().then((resp) => {
      setPasswordPolicies(
        resp.filter((item) => {
          return item.value !== null;
        }),
      );
    });
  };

  useEffect(() => {
    fetchPasswordPolicies();
  }, []);

  /**
   * Event handler for the current password field change.
   * @param {object} event - The change event object.
   */
  const handlePasswordAtualChange = (event) => {
    setPasswordAtual(event.target.value);
  };

  /**
   * Event handler for the new password field change.
   * @param {object} event - The change event object.
   */
  const handlePasswordNovaChange = (event) => {
    setPasswordNova(event.target.value);
  };

  /**
   * Event handler for the confirm password field change.
   * @param {object} event - The change event object.
   */
  const handlePasswordConfirmarChange = (event) => {
    setPasswordConfirmar(event.target.value);
  };

  /**
   * Checks if all password rules are valid.
   * @returns {boolean} - True if all rules are valid, false otherwise.
   */
  const areAllRulesValid = () => {
    return passwordPolicies.every((record) => {
      return isRuleValid(record);
    });
  };

  /**
   * Checks if a specific password rule is valid.
   * @param {object} record - The password policy record.
   * @returns {boolean} - True if the rule is valid, false otherwise.
   */
  const isRuleValid = (record) => {
    let isValid = false;

    if (record.value?.startsWith('REGEXP=')) {
      const regex = new RegExp(record.value.replace('REGEXP=', ''));
      isValid = regex.test(passwordNova);
    }

    return isValid;
  };

  /**
   * Renders the icons for the password rules.
   * @returns {JSX.Element[]} - The array of icon elements.
   */
  const renderRuleIcons = () => {
    return passwordPolicies.map((record) => {
      const isValid = isRuleValid(record);

      return (
        <div style={{ display: 'flex', paddingBottom: 12 }} key={record.id}>
          <FontAwesomeIcon
            icon={isValid ? faCircleCheck : faCircleXmark}
            color={isValid ? '#06888f' : '#dc4040'}
            style={{ paddingRight: '8px' }}
          />
          <TypographyTextStyled>{record.descr}</TypographyTextStyled>
        </div>
      );
    });
  };

  const username = useSelector(selectUserId);
  const handleSubmit = async (values, { setSubmitting }) => {
    setFeedbackMsg(null);
    const data = await changePasswordServices.salt(username);
    const { salt } = data[0];
    const oldHash = cryptoLogin(passwordAtual, salt);

    const newHash = await cryptoSignUp(passwordNova);

    changePasswordServices
      .changePassword({
        username,
        oldHashPassword: oldHash.hashPassword,
        newHashPassword: newHash.hashPassword,
        newSalt: newHash.salt,
        otp: validationCode,
      })
      .then(() => {
        setSubmittedWithSuccess(true);
        setFeedbackMsg('TRLT_UI-PASSWORD_CHANGED');
        setSubmitting(false);
      })
      .catch((error) => {
        setSubmittedWithSuccess(false);
        const [{ detail }] = error;
        setFeedbackMsg(detail);
        setSubmitting(false);
      });
  };

  const getFeedbackMessage = () => {
    if (feedbackMsg)
      return (
        <FeedbackContainer>
          <TrlFeedback
            title={
              submittedWithSuccess
                ? 'TRLT_UI-SUCESS'
                : 'TRLT_UI-PASSWORD_ERRORS'
            }
            msg={feedbackMsg}
            success={submittedWithSuccess}
          />
        </FeedbackContainer>
      );

    return null;
  };

  return (
    <>
      {isMobile ? (
        <TrlScreenSummary title="TRLT_UI-CHANGE_PASSWORD" hasTopMargin>
          {getFeedbackMessage()}
          <TrlTextWithLink mainText="TRLT_UI-PASSWORD_WARNING" />
        </TrlScreenSummary>
      ) : (
        <>
          {getFeedbackMessage()}
          <TrlTextWithLink mainText="TRLT_UI-PASSWORD_WARNING" />
        </>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: useMediaQuery(theme.breakpoints.down('lg'))
            ? 'column-reverse'
            : 'row',
          gap: isMobile ? '16px' : '32px',
        }}
      >
        <Formik
          initialValues={{
            passwordAtual,
            passwordNova,
            passwordConfirmar,
          }}
          onSubmit={handleSubmit}
          validationSchema={trlChangePasswordValidation()}
          enableReinitialize
        >
          {(data) => (
            <Form>
              <TrlTextField
                name="passwordAtual"
                type="password"
                label={<FormattedMessage id="TRLT_UI-CURRENT_PASSWORD" />}
                id="passwordAtual"
                value={passwordAtual}
                autoComplete="off"
                margin="normal"
                additionalFunc={handlePasswordAtualChange}
              />
              <TrlTextField
                name="passwordNova"
                type="password"
                label={<FormattedMessage id="TRLT_UI-NEW_PASSWORD" />}
                id="passwordNova"
                value={passwordNova}
                autoComplete="off"
                margin="normal"
                additionalFunc={handlePasswordNovaChange}
              />
              <TrlTextField
                name="passwordConfirmar"
                type="password"
                label={<FormattedMessage id="TRLT_UI-CONFIRM_PASSWORD" />}
                id="passwordConfirmar"
                value={passwordConfirmar}
                autoComplete="off"
                margin="normal"
                additionalFunc={handlePasswordConfirmarChange}
              />
              {useOtp && (
                <Otp
                  margin="16px 0 0 0"
                  value={validationCode}
                  onChangeValue={handleValueChange}
                  onError={handleGetOptError}
                />
              )}
              <ButtonMain
                expressionId="TRLT_UI-CHANGE"
                type="submit"
                id="submitAlterar"
                sx={{
                  width: '100%',
                  marginTop: '16px',
                  marginBottom: isMobile && '16px',
                }}
                disabled={
                  data.isSubmitting ||
                  !(data.isValid && validOTP) ||
                  !areAllRulesValid()
                }
              />
            </Form>
          )}
        </Formik>
        <BoxStyled>
          {!isMobile && (
            <>
              <TypographyTitleStyled>
                <FormattedMessage id="TRLT_UI-PW_REQUIREMENTS" />
              </TypographyTitleStyled>
              <TypographySubtitleStyled>
                <FormattedMessage id="TRLT_UI-PW_CONDITION" />
              </TypographySubtitleStyled>
            </>
          )}
          {renderRuleIcons()}
        </BoxStyled>
      </div>
    </>
  );
};
